import clsx from "clsx";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { ExpandableText } from "./ExpandableText";
import { DeltaIndicator } from "./DeltaIndicator";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { InfoTooltip } from "@/ui/molecules/InfoTooltip";
import { MetricSource } from "@/features/Metrics/Metrics.types";
import { DataSource, SourceTag } from "./SourceTag";

export enum PeriodKind {
    LTM = "LTM",
    NTM = "NTM",
    LAST_CLOSED_MONTH = "Last Closed Month",
}

const PERIOD_DESCRIPTIONS: Record<PeriodKind, string> = {
    [PeriodKind.LTM]: "Data is as of the last twelve months.",
    [PeriodKind.NTM]: "Data is as of the next twelve months.",
    [PeriodKind.LAST_CLOSED_MONTH]:
        "Balance Sheet data is surfaced as of the last closed calendar month.",
};

export interface DetailTableRow {
    label: string | React.ReactNode;
    value: string | number | React.ReactNode;
    onClick?: () => void;
    onDeltaClick?: () => void;
    valueClassName?: string;
    source?: MetricSource | DataSource | null;
    fieldName?: string;
    delta?: number | null;
}

interface DetailTableProps {
    title: string;
    data: DetailTableRow[];
    className?: string;
    icon?: React.ReactNode;
    href?: string;
    isLoading?: boolean;
    asOfDate?: string;
    periodKind?: PeriodKind;
}

export function DetailTable({
    title,
    data,
    className,
    icon,
    href,
    asOfDate,
    isLoading = false,
    periodKind,
}: DetailTableProps) {
    const flags = useFlags();
    const showM05 = flags.enabled("show-im-m05");
    return (
        <div className={clsx("w-full", className)}>
            <div className="flex items-center justify-between">
                <h4 className="heading-m mb-3">{title}</h4>

                {periodKind && (
                    <div className="tt-trigger group">
                        <CalendarDaysIcon className="size-4 stroke-gray-400" />
                        <p className="body-micro text-gray-400">{periodKind}</p>
                        <div className="tt-container tt-bottom-custom">
                            <div className="tt w-48">
                                {PERIOD_DESCRIPTIONS[periodKind]}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {asOfDate && (
                <div
                    className={clsx(
                        "mb-3 mt-2",
                        "body-s flex items-center gap-1",
                    )}
                >
                    <span className="subdued italic">
                        Data as of {asOfDate}
                    </span>
                    <InfoTooltip>
                        The data displayed is from the last closed, complete
                        calendar period available in the company&apos;s
                        accounting system. Hum does not display partial period
                        data.
                    </InfoTooltip>
                </div>
            )}
            <div
                className={clsx(
                    "grid grid-cols-1",
                    "gap-0 overflow-hidden",
                    "sm:grid-cols-2 sm:gap-x-4",
                )}
            >
                {data.map((row, index) => {
                    const rowInGrid = Math.floor(index / 2);
                    const isEvenRow = rowInGrid % 2 === 0;

                    return (
                        <div
                            key={index}
                            className={clsx(
                                "flex items-center justify-between gap-4 border-x border-slate-700 px-4 py-3",
                                isEvenRow ? "bg-[#2E3849]" : "bg-[#353E4F]",
                                row.onClick &&
                                    "cursor-pointer hover:opacity-90",
                                index === 0 &&
                                    "rounded-t border-x border-t border-slate-700",
                                index === 1 &&
                                    "border-x border-t border-slate-700 sm:rounded-t",
                                index === data.length - 1 &&
                                    "rounded-b border-x border-b border-slate-700",
                                index === data.length - 2 &&
                                    "border-x border-b border-slate-700 sm:rounded-b",
                            )}
                        >
                            <span
                                className={clsx(
                                    "heading-s cursor-pointer break-words",
                                    row.delta !== undefined && showM05
                                        ? "w-1/3"
                                        : "w-1/2",
                                )}
                            >
                                {row.label}
                            </span>
                            {href && (
                                <div
                                    className={clsx(
                                        "group flex items-start gap-2",
                                        row.delta !== undefined && showM05
                                            ? "w-1/3"
                                            : "w-1/2",
                                    )}
                                >
                                    <a
                                        href={href}
                                        className="text-link break-words"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {row.value}
                                    </a>
                                    {icon && icon}
                                </div>
                            )}
                            {!href && (
                                <div
                                    className={clsx(
                                        "flex flex-col items-start",
                                        row.delta !== undefined && showM05
                                            ? "w-1/3"
                                            : "w-1/2",
                                    )}
                                    onClick={row.onClick}
                                >
                                    {isLoading ? (
                                        <div
                                            className={clsx(
                                                "h-3 w-16 bg-slate-700",
                                                "animate-pulse rounded",
                                            )}
                                        />
                                    ) : (
                                        <>
                                            {typeof row.value === "string" ? (
                                                <div className="body-s cursor-pointer text-left">
                                                    <ExpandableText
                                                        text={row.value}
                                                        maxLength={200}
                                                        className={clsx(
                                                            row.value ===
                                                                COMING_SOON &&
                                                                "text-disabled",
                                                            "cursor-pointer",
                                                            row.valueClassName,
                                                        )}
                                                    />
                                                </div>
                                            ) : (
                                                <span
                                                    className={clsx(
                                                        "body-s break-words text-left",
                                                        row.valueClassName,
                                                    )}
                                                >
                                                    {row.value}
                                                </span>
                                            )}
                                            <SourceTag source={row.source} />
                                        </>
                                    )}
                                </div>
                            )}
                            {row.delta !== undefined && (
                                <div
                                    className={clsx(
                                        "flex w-1/3 cursor-pointer items-end justify-end",
                                        row.onDeltaClick && "cursor-pointer",
                                    )}
                                    onClick={row.onDeltaClick}
                                >
                                    {isLoading ? (
                                        <div
                                            className={clsx(
                                                "h-3 w-12 bg-slate-700",
                                                "animate-pulse rounded",
                                            )}
                                        />
                                    ) : (
                                        <DeltaIndicator delta={row.delta} />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
